import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  ActivatedRoute,
  provideRouter,
  Router,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';
import {
  CcdlSpinnerService,
  SpinnerOptions,
  SpinnerType,
} from '@cpm-pr1618001/ng-design-lib';
import {
  AppConfig,
  AppInitializerEffects,
  AppInitializerFacade,
  appInitializerFeature,
  CpmGlobalErrorHandler,
  DashboardLabelsEffects,
  dashboardLabelsFeature,
  HttpInterceptorsFn,
  HttpRequestStatus,
  RefreshTokenResponseWithStatus,
} from '@cpm-pr1618003/ng-initializer-lib';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { LandingPageEffects } from './landing-page/store/effects/landing-page.effects';
import { landingPageFeature } from './landing-page/store/feature/landing-page.feature';
import { InitEffects } from './store/effects/init.effects';
import { initFeature } from './store/feature/init.feature';

export const initializeApp = (): any => {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const facade = inject<AppInitializerFacade>(AppInitializerFacade);
  const ccdlSpinnerService = inject(CcdlSpinnerService);
  const spinnerOptions: SpinnerOptions = {
    type: SpinnerType.DUAL_RING,
    spinnerText: 'Application initializing...',
  };
  // Initializing parameters and required run on local
  const appConfig: AppConfig = {
    excludeUrls: [
      '/auth/app-initializer',
      '/auth/refresh-token',
      '/auth/silent-refresh-token',
    ],
    bypassExcludeUrl: false,
    baseUrl: environment.baseUrl,
    redirectUrl: environment.cuiAppUrl,
    appInitRequest: {
      appCode: '134002',
      appName: 'capvizon-ui',
    },
    shouldHandle401Errors: false,
  };
  facade.setAppConfig(appConfig);

  // This spinner should be called only once at application level and it closes/detaches view when app-root element found.
  ccdlSpinnerService.open(spinnerOptions);
  const appPromise$: Promise<boolean> = new Promise((resolve, reject) => {
    facade.loadAppInitializer();
    facade.loadRefreshToken();
    facade.getRefreshTokenData$
      .pipe(
        filter(
          (refreshToken) => refreshToken !== null && refreshToken !== undefined,
        ),
      )
      .subscribe((refreshToken: RefreshTokenResponseWithStatus) => {
        if (
          refreshToken.userId &&
          refreshToken.isRefreshTokenValid &&
          refreshToken.refreshTokenRequestStatus === HttpRequestStatus.Success
        ) {
          // External redirect: go to finmui application
          window.location.href = environment.captrixonURL;
          // Do NOT resolve, as we are leaving the app
        }
        resolve(true);
      });
    ccdlSpinnerService.close();
  });
  return () => appPromise$;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initializeApp),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation(), withViewTransitions()),
    provideHttpClient(withInterceptors([HttpInterceptorsFn])),
    provideStore(),
    provideState(initFeature),
    provideEffects([
      InitEffects,
      LandingPageEffects,
      AppInitializerEffects,
      DashboardLabelsEffects,
    ]),
    provideState(landingPageFeature),
    provideState(appInitializerFeature),
    provideState(dashboardLabelsFeature),
    provideStoreDevtools(environment.storeDevTools),
    {
      provide: ErrorHandler,
      useClass: CpmGlobalErrorHandler,
    },
  ],
};
